/**
 * Multistep Module
 * Multi-step form navigation
 */

import { validateStepInputs } from './validation';

/**
 * Button references for multi-step navigation
 */
export interface MultistepButtons {
    nextBtn: HTMLButtonElement | null;
    prevBtn: HTMLButtonElement | null;
    submitBtn: HTMLButtonElement | null;
}

/**
 * Checks if a form is configured as multi-step via data attributes.
 */
export function isMultiStepForm(form: HTMLFormElement): boolean {
    return form.hasAttribute('data-current-step');
}

/**
 * Gets the current step index (0-based) from the dataset.
 */
export function getCurrentStep(form: HTMLFormElement): number {
    const step = form.dataset.currentStep;
    
    if (step) {
        return parseInt(step, 10);
    }
    
    return 0;
}

/**
 * Gets the total number of steps from the dataset.
 */
export function getTotalSteps(form: HTMLFormElement): number {
    const total = form.dataset.totalSteps;
    
    if (total) {
        return parseInt(total, 10);
    }
    
    return 1;
}

/**
 * Sets up event listeners for prev/next navigation buttons.
 */
export function setupMultistepEvents(
    form: HTMLFormElement,
    buttons: MultistepButtons,
    onStepChange?: () => void
): void {
    if (isMultiStepForm(form) === false) {
        return;
    }

    buttons.prevBtn = form.querySelector<HTMLButtonElement>('button[data-prev-btn]');
    buttons.nextBtn = form.querySelector<HTMLButtonElement>('button[data-next-btn]');

    if (buttons.prevBtn) {
        buttons.prevBtn.addEventListener('click', () => {
            navigateStep(form, -1, buttons);
            
            if (onStepChange) {
                onStepChange();
            }
        });
    }

    if (buttons.nextBtn) {
        buttons.nextBtn.addEventListener('click', () => {
            navigateStep(form, 1, buttons);
            
            if (onStepChange) {
                onStepChange();
            }
        });
    }
}

/**
 * Moves to the next or previous step.
 * Returns true if navigation was successful.
 */
export function navigateStep(
    form: HTMLFormElement,
    direction: 1 | -1,
    buttons: MultistepButtons
): boolean {
    let currentStep = getCurrentStep(form);
    const totalSteps = getTotalSteps(form);

    if (buttons.nextBtn) {
        buttons.nextBtn.disabled = true;
    }

    const stepContents = form.querySelectorAll<HTMLElement>('.step-content > div');

    // Logic: Validate inputs only when moving forward
    if (direction === 1) {
        const currentContent = stepContents[currentStep];
        
        if (validateStepInputs(currentContent) === false) {
            if (buttons.nextBtn) {
                buttons.nextBtn.disabled = false;
            }
            
            return false;
        }
    }

    currentStep = currentStep + direction;

    if (currentStep >= totalSteps || currentStep < 0) {
        if (buttons.nextBtn) {
            buttons.nextBtn.disabled = false;
        }
        
        return false;
    }

    form.dataset.currentStep = String(currentStep);

    // Use a small delay for a cleaner visual transition
    window.setTimeout(() => {
        showStep(form, currentStep, buttons);
        
        if (buttons.nextBtn) {
            buttons.nextBtn.disabled = false;
        }
    }, 150);

    return true;
}

/**
 * Shows a specific step and updates button visibility/progress.
 */
export function showStep(form: HTMLFormElement, stepIndex: number, buttons: MultistepButtons): void {
    const stepContents = form.querySelectorAll<HTMLElement>('.step-content > div:not(.btn-container)');
    const progressSteps = form.querySelectorAll<HTMLElement>('.progress-container .step');

    // Hide all step contents
    stepContents.forEach((step) => {
        step.classList.add('d-none');
        step.classList.remove('active');
    });

    // Show selected step content
    const activeStepContent = stepContents[stepIndex];
    
    if (activeStepContent) {
        activeStepContent.classList.remove('d-none');
        activeStepContent.classList.add('active');
    }

    // Update progress indicators
    progressSteps.forEach((step) => {
        step.classList.remove('active');
        step.classList.add('d-none');
    });

    const activeProgress = progressSteps[stepIndex];
    
    if (activeProgress) {
        activeProgress.classList.remove('d-none');
        activeProgress.classList.add('active');
    }

    // Handle Prev Button visibility and layout
    if (buttons.prevBtn) {
        if (stepIndex === 0) {
            buttons.prevBtn.classList.add('d-none');
            
            if (buttons.prevBtn.parentElement) {
                buttons.prevBtn.parentElement.style.display = 'flex';
            }
        } else {
            buttons.prevBtn.classList.remove('d-none');
            
            if (buttons.prevBtn.parentElement) {
                buttons.prevBtn.parentElement.style.display = 'grid';
            }
        }
    }

    // Handle Next/Submit toggle logic
    const totalSteps = getTotalSteps(form);
    if (stepIndex === totalSteps - 1) {
        if (buttons.nextBtn) {
            buttons.nextBtn.classList.add('d-none');
        }
        if (buttons.submitBtn) {
            buttons.submitBtn.classList.remove('d-none');
        }
    } else {
        if (buttons.nextBtn) {
            buttons.nextBtn.classList.remove('d-none');
        }
        if (buttons.submitBtn) {
            buttons.submitBtn.classList.add('d-none');
        }
    }
}

/**
 * Handles the final UI state (success/failure) after submission.
 */
export function showFinalStep(form: HTMLFormElement, status: 'success' | 'failed'): void {
    const progressContainer = form.querySelector<HTMLElement>('.progress-container');
    
    if (progressContainer) {
        progressContainer.classList.add('d-none');
    }

    form.querySelectorAll<HTMLElement>('.step-content > div:not(.btn-container)').forEach((step) => {
        step.classList.add('d-none');
        step.classList.remove('active');
    });

    const btnContainer = form.querySelector<HTMLElement>('.btn-container');
    
    if (btnContainer) {
        btnContainer.classList.add('d-none');
    }

    const finalStep = form.querySelector<HTMLElement>('.step-content > div[data-step="finale"]');
    
    if (finalStep) {
        const messageEl = finalStep.querySelector<HTMLElement>(`.${status}-message`);
        
        if (messageEl) {
            messageEl.classList.remove('d-none');
        }
        
        finalStep.classList.remove('d-none');
        finalStep.classList.add('active');
    }
}